// src/pages/financeiro/FinanceiroContratosPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, deleteDoc, doc, getDocs, query, serverTimestamp, updateDoc, where } from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { useSortableTable, SortableHeader } from "../../hooks/useSortableTable";
import { formatData } from "../../utils/dateFormat";
import { db } from "../../config/firebase";
import { sincronizarPlanejamentoContratoAprovado } from "../../services/abrirPreventivasClienteApi";

const COLLECTION_CONTRATOS = "financeiro_contratos";

const STATUS_OPTIONS = [
  { value: "CRIADO", label: "Criado" },
  { value: "ENVIADO", label: "Enviado" },
  { value: "APROVADO", label: "Aprovado" },
  { value: "REPROVADO", label: "Reprovado" },
];

export default function FinanceiroContratosPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const navigate = useNavigate();

  const [contratos, setContratos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [campoFiltro, setCampoFiltro] = useState("clienteNome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [deletandoId, setDeletandoId] = useState(null);
  const [salvandoStatusId, setSalvandoStatusId] = useState(null);
  const [sincronizandoAprovados, setSincronizandoAprovados] = useState(false);

  useEffect(() => {
    const carregarContratos = async () => {
      if (!usuario?.grupoId) return;
      setCarregando(true);
      setErro("");
      try {
        const nivel = nivelUsuario(usuario);
        const restringirEmpresa = !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
        let qBase = query(
          collection(db, COLLECTION_CONTRATOS),
          where("grupoId", "==", usuario.grupoId)
        );

        if (restringirEmpresa) {
          const empresaId = empresaAtual?.id || usuario?.empresaId || null;
          if (empresaId) {
            qBase = query(
              collection(db, COLLECTION_CONTRATOS),
              where("grupoId", "==", usuario.grupoId),
              where("empresaId", "==", empresaId)
            );
          }
        }

        const snap = await getDocs(qBase);
        const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setContratos(lista);
      } catch (e) {
        console.error("Erro ao carregar contratos:", e);
        setErro("Erro ao carregar contratos.");
        setContratos([]);
      } finally {
        setCarregando(false);
      }
    };

    carregarContratos();
  }, [usuario?.grupoId, usuario?.empresaId, empresaAtual?.id, modoGlobal]);

  const camposFiltro = [
    { value: "clienteNome", label: "Cliente" },
    { value: "contratoTipo", label: "Tipo" },
    { value: "status", label: "Status" },
    { value: "dataInicial", label: "Data inicial" },
    { value: "academias", label: "Academias" },
  ];

  const contratosFiltrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return contratos;
    return contratos.filter((c) => {
      let base = "";
      if (campoFiltro === "academias") {
        base = (c.academias || []).map((a) => a.nome || a.id).join(" ");
      } else if (campoFiltro === "dataInicial") {
        base = formatData(c.dataInicial) || "";
      } else {
        base = c[campoFiltro] || "";
      }
      return String(base).toLowerCase().includes(texto);
    });
  }, [contratos, campoFiltro, textoFiltro]);

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    contratosFiltrados,
    "clienteNome"
  );

  const handleExcluirContrato = async (contrato) => {
    if (!contrato?.id) return;
    const confirmar = window.confirm("Deseja excluir este contrato?");
    if (!confirmar) return;
    setDeletandoId(contrato.id);
    setErro("");
    try {
      await deleteDoc(doc(db, COLLECTION_CONTRATOS, contrato.id));
      setContratos((prev) => prev.filter((c) => c.id !== contrato.id));
    } catch (e) {
      console.error("Erro ao excluir contrato:", e);
      setErro("Erro ao excluir contrato.");
    } finally {
      setDeletandoId(null);
    }
  };

  const handleAlterarStatusContrato = async (contrato, novoStatus) => {
    if (!contrato?.id || !novoStatus) return;
    if (String(novoStatus) === String(contrato.status || "")) return;

    setSalvandoStatusId(contrato.id);
    setErro("");
    setSucesso("");
    try {
      const aprovandoAgora = String(novoStatus || "").toUpperCase() === "APROVADO";
      const aprovadoEmRef = aprovandoAgora ? new Date() : null;
      await updateDoc(doc(db, COLLECTION_CONTRATOS, contrato.id), {
        status: novoStatus,
        aprovadoEm: aprovandoAgora ? aprovadoEmRef : (contrato?.aprovadoEm || null),
        updatedAt: serverTimestamp(),
      });

      if (aprovandoAgora) {
        try {
          await sincronizarPlanejamentoContratoAprovado({
            grupoId: usuario?.grupoId,
            clienteId: contrato?.clienteId,
            clienteNome: contrato?.clienteNome || "",
            empresaId: contrato?.empresaId || contrato?.clienteId || null,
            contrato: { ...contrato, status: "APROVADO", aprovadoEm: aprovadoEmRef },
            criadoPor: usuario?.uid || usuario?.id || "",
            aprovadoEm: aprovadoEmRef,
          });
        } catch (errSync) {
          console.warn("Falha ao sincronizar planejamento do contrato aprovado:", errSync);
        }
      }

      setContratos((prev) => prev.map((item) => (
        item.id === contrato.id ? { ...item, status: novoStatus } : item
      )));
    } catch (e) {
      console.error("Erro ao atualizar status do contrato:", e);
      setErro("Erro ao atualizar status do contrato.");
    } finally {
      setSalvandoStatusId(null);
    }
  };

  const handleSincronizarAprovados = async () => {
    const aprovados = contratos.filter((c) => String(c?.status || "").toUpperCase() === "APROVADO");
    if (!aprovados.length) {
      setErro("");
      setSucesso("Não há contratos aprovados para sincronizar.");
      return;
    }

    const confirmar = window.confirm(
      `Sincronizar ${aprovados.length} contrato(s) aprovado(s) com o Planejamento?\n\n` +
      "Isso vai abrir/preparar preventivas e competências futuras para agendamento."
    );
    if (!confirmar) return;

    setSincronizandoAprovados(true);
    setErro("");
    setSucesso("");

    let ok = 0;
    let falhas = 0;
    let atendimentosCriados = 0;
    let sessoesCriadas = 0;
    let itensCriados = 0;

    for (const contrato of aprovados) {
      try {
        const aprovadoEmContrato = contrato?.aprovadoEm?.toDate
          ? contrato.aprovadoEm.toDate()
          : (contrato?.aprovadoEm ? new Date(contrato.aprovadoEm) : null);
        const aprovadoEmRef = (!aprovadoEmContrato || Number.isNaN(aprovadoEmContrato.getTime()))
          ? new Date()
          : aprovadoEmContrato;

        if (!aprovadoEmContrato || Number.isNaN(aprovadoEmContrato.getTime())) {
          await updateDoc(doc(db, COLLECTION_CONTRATOS, contrato.id), {
            aprovadoEm: aprovadoEmRef,
            updatedAt: serverTimestamp(),
          });
        }

        const res = await sincronizarPlanejamentoContratoAprovado({
          grupoId: usuario?.grupoId,
          clienteId: contrato?.clienteId,
          clienteNome: contrato?.clienteNome || "",
          empresaId: contrato?.empresaId || contrato?.clienteId || null,
          contrato: { ...contrato, aprovadoEm: aprovadoEmRef },
          criadoPor: usuario?.uid || usuario?.id || "",
          aprovadoEm: aprovadoEmRef,
        });
        ok += 1;
        atendimentosCriados += Number(res?.atendimentosCriados || 0);
        sessoesCriadas += Number(res?.sessoesCriadas || 0);
        itensCriados += Number(res?.itensCriados || 0);
      } catch (e) {
        falhas += 1;
        console.warn("Falha na sincronização do contrato aprovado:", contrato?.id, e);
      }
    }

    if (falhas > 0) {
      setErro(`Sincronização concluída com falhas (${ok} ok / ${falhas} falha(s)).`);
    } else {
      setErro("");
    }
    setSucesso(
      `Backfill concluído: ${ok} contrato(s) processado(s), ` +
      `${atendimentosCriados} atendimento(s), ${sessoesCriadas} sessão(ões) e ${itensCriados} item(ns) criados.`
    );
    setSincronizandoAprovados(false);
  };

  return (
    <LayoutDesktop tema={tema} titulo="Contratos" subtitulo="Financeiro / Cadastros / Contratos">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Contratos cadastrados</h3>
            <p>Lista de contratos vinculados ao grupo atual.</p>
          </div>
          <div className="pc-table-header-actions">
            <button
              type="button"
              className="pc-btn-secundario"
              onClick={handleSincronizarAprovados}
              disabled={carregando || sincronizandoAprovados}
              title="Sincronizar contratos já aprovados"
            >
              {sincronizandoAprovados ? "Sincronizando..." : "Sincronizar aprovados"}
            </button>
            <button
              type="button"
              className="pc-btn-primario"
              onClick={() => navigate("/financeiro/contratos/novo")}
            >
              + Novo contrato
            </button>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar contratos..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {sucesso && (
            <div
              style={{
                background: "#064e3b",
                color: "#d1fae5",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {sucesso}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando contratos...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhum contrato encontrado.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <SortableHeader
                    label="Cliente"
                    sortKey="clienteNome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("clienteNome")}
                  />
                  <SortableHeader
                    label="Tipo"
                    sortKey="contratoTipo"
                    onSort={requestSort}
                    sortDirection={getSortDirection("contratoTipo")}
                  />
                  <SortableHeader
                    label="Data inicial"
                    sortKey="dataInicial"
                    onSort={requestSort}
                    sortDirection={getSortDirection("dataInicial")}
                  />
                  <th>Status</th>
                  <th>Duração</th>
                  <th>Visitas emergenciais</th>
                  <th style={{ width: 160 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((c) => {
                  const academiasTexto = (c.academias || [])
                    .map((a) => a.nome || a.id)
                    .filter(Boolean)
                    .join(", ");
                  const aberto = expandedId === c.id;

                  return (
                    <React.Fragment key={c.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={aberto ? "Recolher" : "Expandir"}
                            onClick={() => setExpandedId((prev) => (prev === c.id ? null : c.id))}
                          >
                            <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>▸</span>
                          </button>
                        </td>
                        <td>{c.clienteNome || "-"}</td>
                        <td>{c.contratoTipo || "-"}</td>
                        <td>{formatData(c.dataInicial) || "-"}</td>
                        <td>
                          {STATUS_OPTIONS.find((s) => s.value === c.status)?.label || c.status || "-"}
                        </td>
                        <td>{c.duracaoMeses != null ? `${c.duracaoMeses} meses` : "-"}</td>
                        <td>{c.visitasEmergenciais != null ? c.visitasEmergenciais : "-"}</td>
                        <td>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <select
                              value={c.status || "CRIADO"}
                              className="pc-input"
                              title="Alterar status"
                              disabled={deletandoId === c.id || salvandoStatusId === c.id}
                              onChange={(e) => handleAlterarStatusContrato(c, e.target.value)}
                              style={{ minWidth: 120, height: 32, padding: "4px 8px" }}
                            >
                              {STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="pc-btn-icon"
                              title="Editar"
                              onClick={() => navigate(`/financeiro/contratos/editar/${c.id}`)}
                              disabled={deletandoId === c.id || salvandoStatusId === c.id}
                              style={{ padding: 6 }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="pc-btn-icon"
                              title="Excluir"
                              onClick={() => handleExcluirContrato(c)}
                              disabled={deletandoId === c.id || salvandoStatusId === c.id}
                              style={{ padding: 6, color: deletandoId === c.id ? "#94a3b8" : "#ef4444" }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      {aberto ? (
                        <tr className="pc-expanded-row">
                          <td colSpan={8}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Academias</div>
                                  <div className="pc-expand-value">{academiasTexto || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Cliente</div>
                                  <div className="pc-expand-value">{c.clienteNome || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Tipo de contrato</div>
                                  <div className="pc-expand-value">{c.contratoTipo || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Status</div>
                                  <div className="pc-expand-value">
                                    {STATUS_OPTIONS.find((s) => s.value === c.status)?.label || c.status || "-"}
                                  </div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Data inicial</div>
                                  <div className="pc-expand-value">{formatData(c.dataInicial) || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Duração</div>
                                  <div className="pc-expand-value">
                                    {c.duracaoMeses != null ? `${c.duracaoMeses} meses` : "-"}
                                  </div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Visitas emergenciais</div>
                                  <div className="pc-expand-value">
                                    {c.visitasEmergenciais != null ? c.visitasEmergenciais : "-"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}
